import * as React from 'react'
import { View, Text } from 'react-native'
import styled from '@theme/styled-components'

import MultiChoice from '@components/choice/MultiChoice'
import Loader from '@components/loader/Loader'

import useI18n from '@i18n/useI18n'

import api from '@api/api'

import { FormikHelpers, useFormik } from 'formik'

import { sanitizeScopeKey } from '@screens/admin/roles/utils'

interface Values {
  scopes: string[]
}

const Scope = ({ form, onForm }: AdminRoleFormProps) => {
  const init: Values = React.useMemo(() => ({ scopes: form.scopes }), [form.scopes])

  const i18n = useI18n()

  const [scopes, setScopes] = React.useState<string[]>([])
  const [status, setStatus] = React.useState<'loading' | 'fetched'>('loading')

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (form.typeUtilisateur) {
      setStatus('loading')

      api.roles
        .scopes(form.typeUtilisateur)
        .then((list) => setScopes(list.scopes))
        .finally(() => setStatus('fetched'))
    }
  }, [form.typeUtilisateur])

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const options = React.useMemo(
    () =>
      scopes
        .map((sc) => ({ label: i18n.t(`screens.admin.roles.SCOPE.${sanitizeScopeKey(sc)}`), value: sc }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [scopes]
  )
  const opts = options.filter((o) => values.scopes.includes(o.value))

  return (
    <MainContainer>
      {status === 'loading' ? (
        <Loading>
          <Loader />
        </Loading>
      ) : options.length > 0 ? (
        <MultiChoice
          data={options}
          onItemSelected={(next) =>
            setFieldValue(
              'scopes',
              next.map((o) => o.value)
            )
          }
          value={opts}
          inline={false}
        />
      ) : (
        <MissingInfo>{i18n.t('screens.admin.roles.form.scope.noScope')}</MissingInfo>
      )}
    </MainContainer>
  )
}

export default Scope

const MainContainer = styled(View)`
  margin: 0 ${(props) => (props.theme.breakpoint === 'phone' ? 24 : 48)}px;
  margin-top: 25px;
  flex: 1;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const Loading = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
`
